/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";

import dayjs from "dayjs";
import { Box, Stack, Typography, Tooltip, Divider } from "@mui/material";
import {
  Info,
  AccessTime,
  CalendarToday,
  AttachMoney,
  Repeat,
  DateRange,
  Schedule,
  Visibility,
} from "@mui/icons-material";

import {
  FormDatePicker,
  FormRadioButton,
  FormSelectInput,
  FormTextInput,
  FormTimeInput,
} from "../../../components/form";
import { RequiredField } from "./RequiredField";
import { SubmitActions } from "./SubmitActions";
import { useFormatter } from "../../../utils/useFormatter";
import { useFluxPayContractContext } from "@/contexts/FluxPayContractContext";
import { useContractContext } from "@/contexts/ContractContext";

export function ScheduleSettings({
  onNext,
  defaultValues,
  setCurrentStep,
}: any) {
  const { currentContract } = useContractContext();
  const { hasFluxPayExtension } = useFluxPayContractContext();

  const [timeSchedule, setTimeSchedule] = useState(0);

  const { control, handleSubmit, getValues, setValue } = useForm({
    defaultValues,
    mode: "onChange", // Isso fará a validação acontecer em tempo real
  });
  let watchdatastart = useWatch({ name: "dataStart", control });

  let watchDurationTime = useWatch({ name: "durationTimeDefault", control });

  let watchdataend = useWatch({ name: "dataEnd", control });

  // Verifica se o contrato atual tem uma assinatura fluxpay
  const hasSubscriptionFluxPay =
    currentContract?.[0]?.resource?.identifier?.some(
      (coding: any) =>
        coding.system.includes("contract-type") &&
        coding.value === "fluxPayPlan",
    );

  const optionsDurationDefault = [
    { display: "15 Minutos", value: "00:15" },
    { display: "30 Minutos", value: "00:30" },
    { display: "1 Hora", value: "01:00" },
    { display: "Personalizar", value: "personDuration" },
  ];

  const daysWarningSchedule = [
    { display: "1 semana", value: 7 },
    { display: "2 semanas", value: 14 },
    { display: "3 semanas", value: 21 },
    { display: "1 mês", value: 30 },
    { display: "2 meses", value: 60 },
    { display: "3 meses", value: 90 },
    { display: "6 meses", value: 180 },
  ].filter((element) => {
    if (element.value > timeSchedule) return false;
    if (element.value === timeSchedule) return false;
    return true;
  });

  if (timeSchedule > 0 && timeSchedule <= 180) {
    daysWarningSchedule.push({
      display: "Agenda completa",
      value: timeSchedule,
    });
  }

  const isDateInFuture = (date: any) => {
    if (!date) {
      return false;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date > today;
  };

  const onSubmit = async (data: any) => {
    await onNext({ schedule: data }); // Envia os dados do local para o controle pai
    setCurrentStep((prev: number) => prev + 1);
  };

  useEffect(() => {
    if (watchdatastart === null || watchdataend === null) {
      setTimeSchedule(0);
      return;
    }

    const initialDate = new Date(watchdatastart);
    const endDate = new Date(watchdataend);
    const difference_in_time = initialDate.getTime() - endDate.getTime();
    const days =
      parseInt(Math.abs(difference_in_time / (1000 * 3600 * 24)).toFixed(0)) +
      1;

    setTimeSchedule(days);
  }, [watchdatastart, watchdataend]);

  // Efeito para atualizar o valor default do advanceWarning
  useEffect(() => {
    if (timeSchedule > 0) {
      const maxValue = Math.max(
        ...daysWarningSchedule.map((option) => option.value),
      );
      setValue("advanceWarning", maxValue);
    }
  }, [timeSchedule, daysWarningSchedule]);

  useEffect(() => {
    if (getValues("dataEnd") < getValues("dataStart"))
      setValue("dataEnd", null);
  }, [getValues("dataStart")]);

  return (
    <Stack width="100%" spacing={4}>
      <Typography
        variant="h6"
        fontWeight="600"
        textAlign="center"
        p="16px 0px 16px 0px"
        color="neutral700.main"
      >
        Adicione as configurações e horários que deseja para esta agenda.
      </Typography>
      <Stack spacing={1}>
        <Box display="flex" alignItems="center" gap={1}>
          <CalendarToday color="primary" />
          <Typography variant="h6" fontWeight="600" color="neutral600.main">
            Nome da agenda <RequiredField />
          </Typography>
        </Box>
        <Typography variant="body1" paddingBottom={2}>
          Escolha um nome para esta agenda.
        </Typography>

        <FormTextInput
          name="scheduleName"
          placeholder="Ex: Sala 01, Consultório 01"
          label="Nome"
          control={control}
          autoFocus
          required
        />
      </Stack>

      <Stack spacing={1}>
        <Box display="flex" alignItems="center" gap={1}>
          <Info color="primary" />
          <Typography variant="h6" fontWeight="600" color="neutral600.main">
            Descrição da agenda
          </Typography>
        </Box>
        <Typography variant="body2" paddingBottom={2}>
          Opcional. Esta descrição será exibida para que o pacientes possam
          compreender melhor os detalhes dessa agenda.
        </Typography>

        <FormTextInput
          name="scheduleDescription"
          placeholder="Ex: Atende convênios X/Y. Retorno em até 30 dias. Teleconsulta apenas."
          label="Descrição (opcional)"
          control={control}
          multiline
          minRows={3}
          maxLength={500}
          validate={(value: string) => {
            if (!value) return true;
            if (value.trim().length === 0) return true;
            if (value.trim().length > 500)
              return "A descrição deve ter no máximo 500 caracteres";
            return true;
          }}
        />
      </Stack>
      <Stack spacing={1}>
        <Box display="flex" alignItems="center" gap={1}>
          <DateRange color="primary" />
          <Typography variant="h6" fontWeight="600" color="neutral600.main">
            Duração da agenda <RequiredField />
          </Typography>
        </Box>
        <Typography variant="body2" paddingBottom={2}>
          Intervalo de tempo de existência desta agenda.
        </Typography>

        <Stack direction="row" width="100%" spacing={2}>
          <FormDatePicker
            name="dataStart"
            label="Início da agenda"
            control={control}
            minDate={dayjs()}
            required
          />

          <FormDatePicker
            name="dataEnd"
            label="Fim da agenda"
            control={control}
            required
            minDate={useWatch({ name: "dataStart", control })}
            validate={isDateInFuture}
            readonly={isNaN(watchdatastart) || watchdatastart === null}
          />
        </Stack>
      </Stack>

      <Divider sx={{ my: 3 }} />

      <Stack spacing={1}>
        <Box display="flex" alignItems="center" gap={1}>
          <AccessTime color="primary" />
          <Typography variant="h6" fontWeight="600" color="neutral600.main">
            Duração padrão de cada consulta
          </Typography>
        </Box>
        <Typography variant="body2" paddingBottom={2}>
          Intervalo de tempo disponível para cada agendamento de consulta.
        </Typography>
        <FormRadioButton
          name="durationTimeDefault"
          label=""
          defaultValue="00:30"
          control={control}
          rowOrientation={true}
          selectOptions={optionsDurationDefault}
        />
        {watchDurationTime === "personDuration" && (
          <Box display="flex" alignItems="center" gap={1} mt={1}>
            <FormTimeInput
              name="personDurationTime"
              label="Tempo personalizado"
              control={control}
              defaultValue={dayjs("2000-01-01T01:30").format("HH:mm")}
              validate={(value: any) => {
                if (!value) return false;
                const time = dayjs(value);
                const hours = time.hour();
                const minutes = time.minute();
                const totalMinutes = hours * 60 + minutes;
                return (
                  totalMinutes >= 10 ||
                  "O tempo mínimo para duração é de 10 minutos"
                );
              }}
              required
            />
            <Tooltip title="O tempo mínimo para duração é de 10 minutos">
              <Info color="action" fontSize="small" />
            </Tooltip>
          </Box>
        )}
      </Stack>

      <Divider sx={{ my: 3 }} />

      <Stack direction="row" width="100%" spacing={4}>
        <Stack spacing={1} width="25%">
          <Box display="flex" alignItems="center" gap={1}>
            <Repeat color="primary" />
            <Typography variant="h6" fontWeight="600" color="neutral600.main">
              Dias para retorno {hasSubscriptionFluxPay && <RequiredField />}
            </Typography>
          </Box>
          <Typography variant="body2" paddingBottom={2}>
            Quantidade de dias para retorno do paciente.
          </Typography>
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            <FormTextInput
              control={control}
              required={hasSubscriptionFluxPay}
              label="Número de dias"
              name="returnDays"
              type="number"
              placeholder="Ex: 30"
              validate={(value: string) => {
                if (!value) return true;
                const numericValue = Number(value);
                if (numericValue <= 0) return "O valor mínimo é 1 dia";
                if (
                  watchdatastart &&
                  watchdataend &&
                  numericValue > timeSchedule
                ) {
                  return `O valor máximo é ${timeSchedule} dias (duração total da agenda)`;
                }
                return true;
              }}
            />
          </Box>
        </Stack>

        {hasFluxPayExtension && (
          <Stack spacing={1} width="25%">
            <Box display="flex" alignItems="center" gap={1}>
              <AttachMoney color="primary" />
              <Typography variant="h6" fontWeight="600" color="neutral600.main">
                Valor da consulta {hasSubscriptionFluxPay && <RequiredField />}
              </Typography>
            </Box>
            <Typography variant="body2" paddingBottom={2}>
              Valor cobrado por cada consulta realizada.
            </Typography>
            <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
              <FormTextInput
                control={control}
                required={hasSubscriptionFluxPay}
                label="Valor (R$)"
                name="serviceCost"
                placeholder="Ex: R$ 150,00"
                mask={useFormatter.formatCurrency}
                validate={(value: string) => {
                  if (!value || value === "") {
                    return hasSubscriptionFluxPay
                      ? "O valor da consulta é obrigatório"
                      : true;
                  }
                  const numericValue = Number(
                    useFormatter.extractNumericValue(value),
                  );
                  if (numericValue === 0 || value === "R$ 0,00") {
                    return hasSubscriptionFluxPay
                      ? "O valor da consulta é obrigatório"
                      : true;
                  }
                  if (numericValue < 5) {
                    return "O valor mínimo para cada consulta é de R$ 5,00";
                  }
                  return true;
                }}
              />
            </Box>
          </Stack>
        )}
      </Stack>

      {/* Configuração opcional de exposição da agenda em portais/integrações */}
      <Stack spacing={1}>
        <Box display="flex" alignItems="center" gap={1}>
          <Visibility color="primary" />
          <Typography variant="h6" fontWeight="600" color="neutral600.main">
            Exposição desta agenda
          </Typography>
        </Box>
        <Typography variant="body2" paddingBottom={2}>
          Defina se esta agenda será pública (visível em portais públicos e
          integrações compatíveis) ou privada (usada apenas internamente na
          clínica).
        </Typography>
        <FormRadioButton
          name="scheduleExposureEnabled"
          label=""
          defaultValue={false}
          control={control}
          rowOrientation={true}
          selectOptions={[
            {
              display: "Agenda pública (exposta em portais)",
              value: true,
            },
            {
              display: "Agenda privada (apenas uso interno)",
              value: false,
            },
          ]}
        />
      </Stack>

      <Divider sx={{ my: 3 }} />

      {watchdatastart && watchdataend && (
        <Stack spacing={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <Schedule color="primary" />
            <Typography variant="h6" fontWeight="600" color="neutral600.main">
              Antecedência de liberação de vagas
            </Typography>
          </Box>
          <Typography variant="body2" paddingBottom={2}>
            Antecedência em que as vagas para agendamento de consultas serão
            liberadas para os pacientes.
          </Typography>
          <Box>
            <FormSelectInput
              name="advanceWarning"
              label="Período em que as vagas devem ser liberadas"
              control={control}
              selectOptions={daysWarningSchedule}
            />
          </Box>
        </Stack>
      )}

      <SubmitActions
        goBack={() => {
          onNext();
          setCurrentStep((prev: number) => prev - 1);
        }}
        goForward={handleSubmit(onSubmit)}
        buttonText="Salvar"
      />
    </Stack>
  );
}
